import React, { useState } from 'react';
import { AlertCircle, CheckCircle, Upload, Eye, X } from 'lucide-react';

const HomeworkSubmissionInterface = ({ homework, onSubmissionComplete, onClose }) => {
  const [selectedImages, setSelectedImages] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const handleImageSelect = (e) => {
    const files = Array.from(e.target.files);
    
    const validFiles = [];
    const newPreviews = [];
    let hasError = false;

    files.forEach(file => {
      if (!['image/jpeg', 'image/jpg', 'image/png'].includes(file.type)) {
        setError(`${file.name} is not a valid image type. Only JPEG, JPG, and PNG are allowed.`);
        hasError = true;
        return;
      }

      validFiles.push(file);
      
      const reader = new FileReader();
      reader.onloadend = () => {
        newPreviews.push({ file, preview: reader.result });
        if (newPreviews.length === validFiles.length) {
          setImagePreviews(prev => [...prev, ...newPreviews]);
        }
      };
      reader.readAsDataURL(file);
    });

    if (!hasError) {
      setSelectedImages(prev => [...prev, ...validFiles]);
      setError(null);
    }
  };

  const removeImage = (index) => {
    setSelectedImages(prev => prev.filter((_, i) => i !== index));
    setImagePreviews(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmitHomework = async () => {
    if (selectedImages.length === 0) {
      setError('Please select at least one image to submit.');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const formData = new FormData();
      selectedImages.forEach(image => {
        formData.append('images', image);
      });

      const token = localStorage.getItem('auth_token');
      
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/homework/student/they/${homework.id}/submissions`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formData
        }
      );

      if (!response.ok) {
        let errorMessage = 'Failed to submit homework';
        
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorData.message || errorMessage;
        } catch (e) {
          const text = await response.text();
          errorMessage = text || `${response.status} ${response.statusText}`;
        }
        
        throw new Error(errorMessage);
      }

      const result = await response.json();
      setSuccess(true);
      
      if (onSubmissionComplete) {
        onSubmissionComplete(homework.id);
      }

    } catch (err) {
      console.error('Submission error:', err);
      setError(err.message || 'An error occurred while submitting homework');
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetSubmission = () => {
    setSelectedImages([]);
    setImagePreviews([]);
    setSuccess(false);
    setError(null);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm">
      <div className="relative w-full max-w-3xl bg-white shadow-2xl rounded-lg overflow-hidden">
        <div className="flex items-center justify-between p-6 border-b bg-gradient-to-r from-blue-50 to-indigo-50">
          <div>
            <h2 className="text-2xl font-bold text-gray-800">
              Submit Homework
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              {homework.subject} - Due {new Date(homework.dueDate).toLocaleDateString()}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-white/50 transition-colors"
            disabled={isSubmitting}
          >
            <X className="w-6 h-6 text-gray-600" />
          </button>
        </div>

        <div className="p-6 max-h-[70vh] overflow-y-auto">
          {!success ? (
            <>
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Upload Your Homework Images
                </label>
                
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-blue-400 transition-colors">
                  <input
                    type="file"
                    accept="image/jpeg,image/jpg,image/png"
                    multiple
                    onChange={handleImageSelect}
                    className="hidden"
                    id="homework-images"
                    disabled={isSubmitting}
                  />
                  <label
                    htmlFor="homework-images"
                    className={`cursor-pointer flex flex-col items-center ${
                      isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                  >
                    <Upload className="w-12 h-12 text-gray-400 mb-3" />
                    <span className="text-sm text-gray-600 mb-1">
                      {isSubmitting ? 'Uploading...' : 'Click to upload or drag and drop'}
                    </span>
                    <span className="text-xs text-gray-500">
                      JPEG, JPG, PNG
                    </span>
                  </label>
                </div>
              </div>

              {imagePreviews.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-sm font-medium text-gray-700 mb-3">
                    Selected Images ({imagePreviews.length})
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {imagePreviews.map((item, index) => (
                      <div key={index} className="relative group">
                        <img
                          src={item.preview}
                          alt={`Upload ${index + 1}`}
                          className="w-full h-40 object-cover rounded-lg border-2 border-gray-200"
                        />
                        <button
                          onClick={() => removeImage(index)}
                          className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                          disabled={isSubmitting}
                        >
                          <X className="w-4 h-4" />
                        </button>
                        <div className="absolute bottom-2 left-2 bg-black/60 text-white text-xs px-2 py-1 rounded">
                          Image {index + 1}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <h4 className="font-medium text-blue-900 mb-2 flex items-center">
                  <AlertCircle className="w-4 h-4 mr-2" />
                  Tips for Best Results
                </h4>
                <ul className="text-sm text-blue-800 space-y-1">
                  <li>• Ensure images are clear and well-lit</li>
                  <li>• Include all pages of the homework</li>
                  <li>• Avoid blurry or out-of-focus photos</li>
                  <li>• Make sure text is readable</li>
                  <li>• Submit all required pages in order</li>
                </ul>
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                  <div className="flex items-start">
                    <AlertCircle className="w-5 h-5 text-red-600 mr-2 flex-shrink-0 mt-0.5" />
                    <div>
                      <h4 className="font-medium text-red-900 mb-1">Error</h4>
                      <p className="text-sm text-red-800">{error}</p>
                    </div>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-8">
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                Homework Submitted Successfully!
              </h3>
              <p className="text-gray-600 mb-6">
                Your homework has been submitted to your tutor for review.
              </p>
              
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 mb-3">Submitted Images</h4>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {imagePreviews.map((item, index) => (
                    <img
                      key={index}
                      src={item.preview}
                      alt={`Submitted ${index + 1}`}
                      className="w-full h-32 object-cover rounded-lg border-2 border-gray-200"
                    />
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="border-t bg-gray-50 p-6">
          <div className="flex justify-between items-center">
            {!success ? (
              <>
                <button
                  onClick={onClose}
                  className="px-6 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-100 transition-colors"
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmitHomework}
                  disabled={isSubmitting || selectedImages.length === 0}
                  className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center"
                >
                  {isSubmitting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Submitting...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4 mr-2" />
                      Submit Homework
                    </>
                  )}
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={resetSubmission}
                  className="px-6 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-100 transition-colors"
                >
                  Submit Another
                </button>
                <button
                  onClick={onClose}
                  className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                >
                  Done
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default HomeworkSubmissionInterface;